import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, ExternalLink } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { FormatOnboardingWizard } from '@/components/ai/FormatOnboardingWizard';

const SAMPLE_TARGET = 200;

interface Retailer {
  id: number;
  name: string;
}

interface VisualClass {
  id: number;
  code: string;
  name: string;
  status: 'ACTIVE' | 'DEPRECATED' | 'DRAFT';
  defaultSupportLevel: 'HUMAN_REVIEW_ONLY' | 'AI_ASSISTED' | 'MODEL_VERIFIED';
  annotationGuidelines?: Record<string, unknown> | null;
  updatedAt?: string | null;
}

interface MappingRow {
  mappingId: number | null;
  retailerId: number;
  retailerName: string;
  mediaFormatId: number;
  mediaFormatName: string;
  visualClassId: number | null;
  visualClassCode: string | null;
  visualClassName: string | null;
  onboardingStatus: 'PROPOSED' | 'MAPPED' | 'TRAINING_NEEDED' | 'ACTIVE' | 'UNSUPPORTED' | 'DEPRECATED' | null;
  supportLevel: 'HUMAN_REVIEW_ONLY' | 'AI_ASSISTED' | 'MODEL_VERIFIED' | null;
  mappingConfidence: number | null;
  decisionReason: string | null;
}

interface OpsMetrics {
  windowHours: number;
  queue: {
    byStatus: Record<string, number>;
    pendingCount: number;
    processingCount: number;
    failedCount: number;
    retryingCount: number;
    oldestPendingAgeSeconds: number;
    avgDoneLatencySeconds: number;
    maxDoneLatencySeconds: number;
  };
  verification: {
    total: number;
    overallStatusCounts: Record<string, number>;
    presenceStatusCounts: Record<string, number>;
    avgConfidence: number;
    insufficientEvidenceCount: number;
    insufficientEvidenceRate: number;
    unknownFormatCount: number;
    unknownFormatRate: number;
    reviewerOverrideCount: number;
    reviewerOverrideRate: number;
  };
}

interface LabelingConfigResponse {
  ok: boolean;
  config: {
    enabled: boolean;
    url: string;
    configured: boolean;
  };
}

const STATUS_OPTIONS: Array<'PROPOSED' | 'MAPPED' | 'TRAINING_NEEDED' | 'ACTIVE' | 'UNSUPPORTED' | 'DEPRECATED'> = [
  'PROPOSED',
  'MAPPED',
  'TRAINING_NEEDED',
  'ACTIVE',
  'UNSUPPORTED',
  'DEPRECATED',
];

const SUPPORT_OPTIONS: Array<'HUMAN_REVIEW_ONLY' | 'AI_ASSISTED' | 'MODEL_VERIFIED'> = [
  'HUMAN_REVIEW_ONLY',
  'AI_ASSISTED',
  'MODEL_VERIFIED',
];

function clampConfidence(value: number): number {
  if (!Number.isFinite(value)) return 0.8;
  return Math.max(0.5, Math.min(0.99, Number(value.toFixed(2))));
}

function getAIStatus(row: MappingRow): 'Active' | 'Learning' | 'Disabled' {
  if (!row.visualClassId || row.onboardingStatus === 'UNSUPPORTED' || row.onboardingStatus === 'DEPRECATED') {
    return 'Disabled';
  }

  if (row.onboardingStatus === 'ACTIVE' && row.supportLevel && row.supportLevel !== 'HUMAN_REVIEW_ONLY') {
    return 'Active';
  }

  return 'Learning';
}

function formatPercent(ratio: number | undefined | null): string {
  const value = Number(ratio ?? 0);
  if (!Number.isFinite(value) || value < 0) return '0.0%';
  return `${(value * 100).toFixed(1)}%`;
}

function formatSeconds(seconds: number | undefined | null): string {
  const value = Number(seconds ?? 0);
  if (!Number.isFinite(value) || value < 0) return '0s';
  if (value >= 3600) return `${(value / 3600).toFixed(1)}h`;
  if (value >= 60) return `${(value / 60).toFixed(1)}m`;
  return `${value.toFixed(0)}s`;
}

function readLabeledImageCount(vc: VisualClass): number {
  const g = vc.annotationGuidelines;
  if (!g || typeof g !== 'object') return 0;

  const candidateKeys = ['labeledImages', 'labelledImages', 'sampleCount', 'trainingImages', 'datasetSize'];
  for (const key of candidateKeys) {
    const value = (g as Record<string, unknown>)[key];
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed >= 0) return Math.trunc(parsed);
  }

  return 0;
}

/** Optional success rate (0–1) from class guidelines when specialists have recorded it. */
function readDetectionSuccessRate(vc: VisualClass | undefined): number | null {
  const g = vc?.annotationGuidelines;
  if (!g || typeof g !== 'object') return null;
  for (const key of ['successfulDetectionRate', 'detectionSuccessRate', 'successRate']) {
    const value = Number((g as Record<string, unknown>)[key]);
    if (Number.isFinite(value) && value >= 0 && value <= 1) return value;
  }
  return null;
}

function recognitionLabel(status: 'Active' | 'Learning' | 'Disabled'): string {
  if (status === 'Active') return 'Recognition enabled';
  if (status === 'Learning') return 'Recognition learning';
  return 'Recognition not enabled';
}

export default function AIVerificationAdminPage() {
  const [activeTab, setActiveTab] = useState<'formats' | 'training' | 'operations'>('formats');
  const [advancedMode, setAdvancedMode] = useState<boolean>(false);

  const [retailers, setRetailers] = useState<Retailer[]>([]);
  const [selectedRetailerId, setSelectedRetailerId] = useState<number | null>(null);
  const [formatsRetailerFilter, setFormatsRetailerFilter] = useState<number | 'all'>('all');
  const [visualClasses, setVisualClasses] = useState<VisualClass[]>([]);
  const [allMappings, setAllMappings] = useState<MappingRow[]>([]);
  const [wizardFormat, setWizardFormat] = useState<MappingRow | null>(null);
  const [settingsFormat, setSettingsFormat] = useState<MappingRow | null>(null);
  const [settingsConfidence, setSettingsConfidence] = useState(0.8);
  const [referenceCountByFormatId, setReferenceCountByFormatId] = useState<Map<number, number>>(new Map());

  const [loading, setLoading] = useState<boolean>(true);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [metricsWindowHours, setMetricsWindowHours] = useState<24 | 168>(24);
  const [opsMetrics, setOpsMetrics] = useState<OpsMetrics | null>(null);
  const [metricsLoading, setMetricsLoading] = useState<boolean>(false);
  const [metricsError, setMetricsError] = useState<string | null>(null);

  const [labelingUrl, setLabelingUrl] = useState<string | null>(null);

  const [newClassCode, setNewClassCode] = useState('');
  const [newClassName, setNewClassName] = useState('');
  const [newClassDescription, setNewClassDescription] = useState('');

  useEffect(() => {
    const loadInitial = async () => {
      try {
        setLoading(true);
        setError(null);

        const [retailersRes, classesRes, labelingRes] = await Promise.all([
          fetch('/api/retailers'),
          fetch('/api/admin/ai-verification/visual-classes'),
          fetch('/api/admin/ai-labeling/config').catch(() => null),
        ]);

        if (!retailersRes.ok) throw new Error(`Failed to load retailers (${retailersRes.status})`);
        if (!classesRes.ok) throw new Error(`Failed to load visual classes (${classesRes.status})`);

        const retailersJson: Retailer[] = await retailersRes.json();
        const classesJson = await classesRes.json();

        setRetailers(retailersJson);
        setVisualClasses(classesJson?.data ?? []);

        if (retailersJson.length > 0) {
          setSelectedRetailerId((prev) => prev ?? retailersJson[0].id);
        }

        if (labelingRes && labelingRes.ok) {
          const labelingJson = await labelingRes.json() as LabelingConfigResponse;
          setLabelingUrl(labelingJson?.config?.url || null);
        }
      } catch (e: any) {
        setError(e?.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadInitial();
  }, []);

  useEffect(() => {
    if (retailers.length === 0) return;

    const loadAllMappings = async () => {
      try {
        const rows = await Promise.all(
          retailers.map(async (retailer) => {
            const res = await fetch(`/api/admin/ai-verification/format-mappings?retailerId=${retailer.id}`);
            if (!res.ok) {
              throw new Error(`Failed to load format mappings (${res.status}) for ${retailer.name}`);
            }
            const json = await res.json();
            return (json?.data ?? []) as MappingRow[];
          }),
        );

        const merged = rows.flat().map((row) => ({
          ...row,
          onboardingStatus: row.onboardingStatus ?? 'PROPOSED',
          supportLevel: row.supportLevel ?? 'HUMAN_REVIEW_ONLY',
          mappingConfidence: row.mappingConfidence ?? 0.8,
        }));

        setAllMappings(merged);
      } catch (e: any) {
        setError(e?.message || 'Failed to load format mappings');
      }
    };

    loadAllMappings();
  }, [retailers]);

  useEffect(() => {
    if (retailers.length === 0) return;

    const loadReferenceCounts = async () => {
      try {
        const maps = await Promise.all(
          retailers.map(async (retailer) => {
            const res = await fetch(`/api/media-formats?retailerId=${retailer.id}&limit=500`);
            if (!res.ok) return [] as Array<{ id: number; count: number }>;
            const json = await res.json();
            const formats = Array.isArray(json)
              ? json
              : Array.isArray(json?.data)
                ? json.data
                : Array.isArray(json?.formats)
                  ? json.formats
                  : [];
            return (formats as Array<{ id?: number; exampleImages?: unknown[]; photoUrl?: string | null }>).map((f) => {
              const examples = Array.isArray(f.exampleImages) ? f.exampleImages.length : 0;
              const fallback = f.photoUrl ? 1 : 0;
              return { id: Number(f.id), count: examples > 0 ? examples : fallback };
            }).filter((row) => Number.isInteger(row.id) && row.id > 0);
          }),
        );

        const next = new Map<number, number>();
        for (const batch of maps) {
          for (const row of batch) next.set(row.id, row.count);
        }
        setReferenceCountByFormatId(next);
      } catch {
        // Reference counts are enrichments; keep page usable without them.
      }
    };

    loadReferenceCounts();
  }, [retailers]);

  useEffect(() => {
    if (!settingsFormat) return;
    setSettingsConfidence(clampConfidence(settingsFormat.mappingConfidence ?? 0.8));
  }, [settingsFormat]);

  useEffect(() => {
    const loadOpsMetrics = async () => {
      try {
        setMetricsLoading(true);
        setMetricsError(null);
        const res = await fetch(`/api/admin/ai-verification/ops/metrics?windowHours=${metricsWindowHours}`);
        if (!res.ok) throw new Error(`Failed to load AI verification metrics (${res.status})`);
        const json = await res.json();
        setOpsMetrics(json?.data ?? null);
      } catch (e: any) {
        setMetricsError(e?.message || 'Failed to load AI verification metrics');
      } finally {
        setMetricsLoading(false);
      }
    };

    loadOpsMetrics();
  }, [metricsWindowHours]);

  const visualClassById = useMemo(() => {
    const map = new Map<number, VisualClass>();
    for (const vc of visualClasses) map.set(vc.id, vc);
    return map;
  }, [visualClasses]);

  const trainingCountByClassId = useMemo(() => {
    const map = new Map<number, number>();
    for (const vc of visualClasses) {
      map.set(vc.id, readLabeledImageCount(vc));
    }
    return map;
  }, [visualClasses]);

  const groupedMappings = useMemo(() => {
    const filtered = formatsRetailerFilter === 'all'
      ? allMappings
      : allMappings.filter((row) => row.retailerId === formatsRetailerFilter);

    const sorted = [...filtered].sort((a, b) => {
      const retailerCompare = a.retailerName.localeCompare(b.retailerName);
      if (retailerCompare !== 0) return retailerCompare;
      return a.mediaFormatName.localeCompare(b.mediaFormatName);
    });

    const map = new Map<string, MappingRow[]>();
    for (const row of sorted) {
      const key = row.retailerName;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(row);
    }
    return Array.from(map.entries());
  }, [allMappings, formatsRetailerFilter]);

  const retailerSummaries = useMemo(() => {
    const summaries = new Map<number, { name: string; total: number; enabled: number }>();
    for (const row of allMappings) {
      if (!summaries.has(row.retailerId)) {
        summaries.set(row.retailerId, { name: row.retailerName, total: 0, enabled: 0 });
      }
      const s = summaries.get(row.retailerId)!;
      s.total++;
      if (getAIStatus(row) === 'Active') s.enabled++;
    }
    return summaries;
  }, [allMappings]);

  const selectedRetailerMappings = useMemo(
    () => allMappings.filter((row) => row.retailerId === selectedRetailerId),
    [allMappings, selectedRetailerId],
  );

  const trainingRows = useMemo(() => {
    return visualClasses
      .filter((vc) => vc.status !== 'DEPRECATED')
      .map((vc) => {
        const labeledImages = trainingCountByClassId.get(vc.id) ?? 0;
        let modelStatus: 'Ready' | 'Needs Data' | 'Training' = 'Needs Data';
        if (labeledImages >= 200) modelStatus = 'Ready';
        else if (labeledImages > 0) modelStatus = 'Training';

        return {
          visualClass: vc,
          labeledImages,
          modelStatus,
          lastUpdated: vc.updatedAt || null,
        };
      })
      .sort((a, b) => a.visualClass.code.localeCompare(b.visualClass.code));
  }, [visualClasses, trainingCountByClassId]);

  const updateMappingInState = (retailerId: number, mediaFormatId: number, patch: Partial<MappingRow>) => {
    setAllMappings((prev) => prev.map((row) => (
      row.retailerId === retailerId && row.mediaFormatId === mediaFormatId
        ? { ...row, ...patch }
        : row
    )));
  };

  const handleSaveMapping = async (row: MappingRow, patch?: Partial<MappingRow>) => {
    const merged = { ...row, ...(patch ?? {}) };

    try {
      setSavingId(row.mediaFormatId);
      setError(null);

      const payload = {
        retailerId: merged.retailerId,
        visualClassId: merged.visualClassId,
        onboardingStatus: merged.onboardingStatus ?? 'PROPOSED',
        supportLevel: merged.supportLevel ?? 'HUMAN_REVIEW_ONLY',
        mappingConfidence: merged.mappingConfidence,
        decisionReason: merged.decisionReason,
        sampleTarget: 200,
        sampleRequirementsJson: {
          minimumReviewVolume: 100,
          retailerDiversityMinimum: 2,
          storeDiversityMinimum: 20,
        },
      };

      const res = await fetch(`/api/admin/ai-verification/format-mappings/${row.mediaFormatId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const message = await res.text();
        throw new Error(`Failed to save mapping (${res.status}): ${message}`);
      }

      updateMappingInState(row.retailerId, row.mediaFormatId, patch ?? {});
    } catch (e: any) {
      setError(e?.message || 'Failed to save mapping');
    } finally {
      setSavingId(null);
    }
  };

  const handleEnableAI = async (row: MappingRow) => {
    setWizardFormat(row);
  };

  const handleDisableAI = async (row: MappingRow) => {
    await handleSaveMapping(row, {
      onboardingStatus: 'UNSUPPORTED',
      supportLevel: 'HUMAN_REVIEW_ONLY',
      decisionReason: row.decisionReason ?? 'Disabled from basic mode',
    });
  };

  const handleCreateClass = async () => {
    if (!newClassCode.trim() || !newClassName.trim()) {
      setError('Class code and name are required');
      return;
    }

    try {
      setError(null);
      const res = await fetch('/api/admin/ai-verification/visual-classes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: newClassCode.trim(),
          name: newClassName.trim(),
          description: newClassDescription.trim() || null,
          status: 'ACTIVE',
          defaultSupportLevel: 'HUMAN_REVIEW_ONLY',
          annotationGuidelines: {},
          qualityProfile: {},
        }),
      });

      if (!res.ok) {
        const message = await res.text();
        throw new Error(`Failed to create class (${res.status}): ${message}`);
      }

      const json = await res.json();
      const upserted = json?.data as VisualClass;
      setVisualClasses((prev) => {
        const without = prev.filter((row) => row.id !== upserted.id);
        return [...without, upserted].sort((a, b) => a.code.localeCompare(b.code));
      });

      setNewClassCode('');
      setNewClassName('');
      setNewClassDescription('');
    } catch (e: any) {
      setError(e?.message || 'Failed to create class');
    }
  };

  const retryRate = useMemo(() => {
    if (!opsMetrics) return 0;
    const denominator = Math.max(1, opsMetrics.queue.pendingCount + opsMetrics.queue.processingCount + opsMetrics.queue.failedCount);
    return opsMetrics.queue.retryingCount / denominator;
  }, [opsMetrics]);

  if (loading) {
    return <div className="p-6">Loading format spotting…</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">How well does REACTOR know our media?</h1>
          <p className="text-sm text-muted-foreground">
            Each store format as an asset — what we know, whether spotting is on, and what still needs attention.
          </p>
        </div>
        <div className="flex items-center gap-2 border rounded-md px-3 py-2">
          <span className="text-xs text-muted-foreground">Simple</span>
          <Switch checked={advancedMode} onCheckedChange={setAdvancedMode} />
          <span className="text-xs font-medium">Show technical detail</span>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'formats' | 'training' | 'operations')}>
        <TabsList>
          <TabsTrigger value="formats">Formats</TabsTrigger>
          <TabsTrigger value="training">Improve from reviews</TabsTrigger>
          <TabsTrigger value="operations">System status</TabsTrigger>
        </TabsList>

        <TabsContent value="formats" className="space-y-4">
          <Alert>
            <AlertTitle>What you do here</AlertTitle>
            <AlertDescription>
              Browse each store format as an asset: what we know about it, whether spotting is on,
              and whether new photos can improve it. Open Recognition settings only when you need to
              turn spotting on/off or change how strict it is.
            </AlertDescription>
          </Alert>

          {error && <div className="text-sm text-red-600">{error}</div>}

          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <CardTitle>Formats</CardTitle>
                <div className="flex items-center gap-3">
                  <Select
                    value={String(formatsRetailerFilter)}
                    onValueChange={(value) => setFormatsRetailerFilter(value === 'all' ? 'all' : Number(value))}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="All retailers" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All retailers</SelectItem>
                      {retailers.map((retailer) => {
                        const summary = retailerSummaries.get(retailer.id);
                        return (
                          <SelectItem key={retailer.id} value={String(retailer.id)}>
                            {retailer.name}{summary ? ` (${summary.enabled}/${summary.total} enabled)` : ''}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {groupedMappings.length === 0 && <p className="text-sm text-muted-foreground">No formats available.</p>}

              {groupedMappings.map(([retailerName, rows]) => {
                const enabledCount = rows.filter((r) => getAIStatus(r) === 'Active').length;
                return (
                <div key={retailerName} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold">{retailerName}</h3>
                    <span className="text-xs text-muted-foreground">
                      {enabledCount} of {rows.length} format{rows.length === 1 ? '' : 's'} with recognition on
                    </span>
                  </div>
                  <div className="grid gap-3">
                    {rows.map((row) => {
                      const aiStatus = getAIStatus(row);
                      const trainingImages = row.visualClassId ? (trainingCountByClassId.get(row.visualClassId) ?? 0) : 0;
                      const referenceImages = referenceCountByFormatId.get(row.mediaFormatId) ?? 0;
                      const successRate = readDetectionSuccessRate(
                        row.visualClassId ? visualClassById.get(row.visualClassId) : undefined,
                      );
                      const improveBy = Math.max(0, SAMPLE_TARGET - trainingImages);
                      const showImproveCta = (aiStatus === 'Active' || aiStatus === 'Learning') && improveBy > 0;

                      return (
                        <div
                          key={`${row.retailerId}-${row.mediaFormatId}`}
                          className="rounded-lg border bg-background p-4 space-y-3"
                        >
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div className="space-y-1 min-w-0">
                              <h4 className="text-lg font-semibold tracking-tight truncate">
                                {row.mediaFormatName}
                              </h4>
                              <p className="text-xs text-muted-foreground">{retailerName}</p>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setSettingsFormat(row)}
                            >
                              Recognition settings
                            </Button>
                          </div>

                          <ul className="space-y-1.5 text-sm">
                            <li className="flex items-center gap-2">
                              <CheckCircle2 className={`h-4 w-4 shrink-0 ${trainingImages > 0 ? 'text-emerald-600' : 'text-muted-foreground'}`} />
                              <span>{trainingImages} training image{trainingImages === 1 ? '' : 's'}</span>
                            </li>
                            <li className="flex items-center gap-2">
                              <CheckCircle2 className={`h-4 w-4 shrink-0 ${referenceImages > 0 ? 'text-emerald-600' : 'text-muted-foreground'}`} />
                              <span>{referenceImages} reference image{referenceImages === 1 ? '' : 's'}</span>
                            </li>
                            <li className="flex items-center gap-2">
                              <CheckCircle2 className={`h-4 w-4 shrink-0 ${aiStatus === 'Active' ? 'text-emerald-600' : 'text-muted-foreground'}`} />
                              <span>{recognitionLabel(aiStatus)}</span>
                            </li>
                            <li className="flex items-center gap-2">
                              <CheckCircle2 className={`h-4 w-4 shrink-0 ${successRate != null ? 'text-emerald-600' : 'text-muted-foreground'}`} />
                              <span>
                                {successRate != null
                                  ? `${Math.round(successRate * 100)}% successful detection`
                                  : 'Successful detection — not enough data yet'}
                              </span>
                            </li>
                          </ul>

                          {showImproveCta && (
                            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 space-y-2">
                              <div className="flex items-start gap-2 text-sm text-amber-950">
                                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                                <div>
                                  <p className="font-medium">Needs attention</p>
                                  <p className="text-amber-900/80">
                                    {improveBy} new photo{improveBy === 1 ? '' : 's'} can improve recognition
                                  </p>
                                </div>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                className="bg-white"
                                onClick={() => {
                                  if (labelingUrl) {
                                    window.open(labelingUrl, '_blank', 'noopener,noreferrer');
                                  } else {
                                    setActiveTab('training');
                                  }
                                }}
                              >
                                {labelingUrl ? (
                                  <>
                                    <ExternalLink className="h-4 w-4 mr-1" />
                                    Review photos
                                  </>
                                ) : (
                                  'Review photos'
                                )}
                              </Button>
                            </div>
                          )}

                          {aiStatus === 'Disabled' && (
                            <div className="flex flex-wrap gap-2">
                              <Button size="sm" onClick={() => handleEnableAI(row)}>
                                Set up spotting
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )})}
            </CardContent>
          </Card>

          {advancedMode && (
            <Card>
              <CardHeader>
                <CardTitle>Technical mappings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Internal mapping/governance controls. Keep Basic Mode for non-technical users.
                </p>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Retailer</label>
                    <Select
                      value={selectedRetailerId ? String(selectedRetailerId) : undefined}
                      onValueChange={(value) => setSelectedRetailerId(Number(value))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select retailer" />
                      </SelectTrigger>
                      <SelectContent>
                        {retailers.map((retailer) => (
                          <SelectItem key={retailer.id} value={String(retailer.id)}>{retailer.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="border rounded-md p-3 space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Create Visual Class</p>
                    <Input placeholder="Code (e.g. AISLE_FIN)" value={newClassCode} onChange={(e) => setNewClassCode(e.target.value)} />
                    <Input placeholder="Display name" value={newClassName} onChange={(e) => setNewClassName(e.target.value)} />
                    <Textarea placeholder="Description" value={newClassDescription} onChange={(e) => setNewClassDescription(e.target.value)} rows={2} />
                    <Button size="sm" onClick={handleCreateClass}>Save visual class</Button>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {visualClasses.map((vc) => (
                    <Badge key={vc.id} variant="outline">{vc.code} · {vc.defaultSupportLevel}</Badge>
                  ))}
                </div>

                <div className="space-y-3">
                  {selectedRetailerMappings.map((row) => (
                    <div key={row.mediaFormatId} className="border rounded-md p-3 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <p className="text-sm font-medium">{row.mediaFormatName}</p>
                          <p className="text-xs text-muted-foreground">Format ID {row.mediaFormatId}</p>
                        </div>
                        <Button size="sm" onClick={() => handleSaveMapping(row)} disabled={savingId === row.mediaFormatId}>
                          {savingId === row.mediaFormatId ? 'Saving...' : 'Save'}
                        </Button>
                      </div>

                      <div className="grid gap-2 md:grid-cols-4">
                        <Select
                          value={row.visualClassId ? String(row.visualClassId) : 'none'}
                          onValueChange={(value) => updateMappingInState(row.retailerId, row.mediaFormatId, {
                            visualClassId: value === 'none' ? null : Number(value),
                          })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Visual class" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">UNKNOWN_FORMAT (review escalator)</SelectItem>
                            {visualClasses.map((vc) => (
                              <SelectItem key={vc.id} value={String(vc.id)}>{vc.code}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <Select
                          value={row.onboardingStatus ?? 'PROPOSED'}
                          onValueChange={(value) => updateMappingInState(row.retailerId, row.mediaFormatId, {
                            onboardingStatus: value as MappingRow['onboardingStatus'],
                          })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Status" />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_OPTIONS.map((status) => (
                              <SelectItem key={status} value={status}>{status}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <Select
                          value={row.supportLevel ?? 'HUMAN_REVIEW_ONLY'}
                          onValueChange={(value) => updateMappingInState(row.retailerId, row.mediaFormatId, {
                            supportLevel: value as MappingRow['supportLevel'],
                          })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Support level" />
                          </SelectTrigger>
                          <SelectContent>
                            {SUPPORT_OPTIONS.map((level) => (
                              <SelectItem key={level} value={level}>{level}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          max="1"
                          value={row.mappingConfidence ?? ''}
                          placeholder="Mapping confidence"
                          onChange={(e) => {
                            const value = e.target.value.trim();
                            updateMappingInState(row.retailerId, row.mediaFormatId, {
                              mappingConfidence: value === '' ? null : Math.max(0, Math.min(1, Number(value))),
                            });
                          }}
                        />
                      </div>

                      <Textarea
                        rows={2}
                        placeholder="Decision reason / notes"
                        value={row.decisionReason ?? ''}
                        onChange={(e) => updateMappingInState(row.retailerId, row.mediaFormatId, { decisionReason: e.target.value })}
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="training" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Improve from reviews</CardTitle>
              <CardDescription>
                Instant photo checks work without extra setup. As reviewers confirm decisions, those photos help Photo Checks get better at spotting each format.
                Open the teaching workspace when you are ready to review examples.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium">
                  Examples collected
                </p>
                <Button
                  variant="outline"
                  onClick={() => {
                    if (labelingUrl) window.open(labelingUrl, '_blank', 'noopener,noreferrer');
                  }}
                  disabled={!labelingUrl}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open teaching workspace
                </Button>
              </div>

              <div className="overflow-x-auto border rounded-md">
                <table className="min-w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Media Type</th>
                      <th className="text-left px-3 py-2 font-medium">Progress</th>
                      <th className="text-left px-3 py-2 font-medium">Status</th>
                      <th className="text-left px-3 py-2 font-medium">Last Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trainingRows.map((row) => {
                      const target = 200;
                      const pct = Math.min(100, Math.round((row.labeledImages / target) * 100));
                      const barColor = row.modelStatus === 'Ready' ? 'bg-green-500' : row.labeledImages > 0 ? 'bg-amber-500' : 'bg-gray-300';

                      const humanName = row.visualClass.name || row.visualClass.code
                        .replace(/_/g, ' ')
                        .replace(/\b\w/g, (c) => c.toUpperCase());

                      return (
                        <tr key={row.visualClass.id} className="border-t">
                          <td className="px-3 py-3">
                            <div>{humanName}</div>
                            <div className="text-xs text-muted-foreground">{row.visualClass.code}</div>
                          </td>
                          <td className="px-3 py-3 min-w-[200px]">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all ${barColor}`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                              <span className="text-xs text-muted-foreground whitespace-nowrap">
                                {row.labeledImages} / {target}
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-3">
                            <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${
                              row.modelStatus === 'Ready'
                                ? 'bg-green-100 text-green-800 border-green-200'
                                : row.modelStatus === 'Training'
                                  ? 'bg-amber-100 text-amber-800 border-amber-200'
                                  : 'bg-gray-100 text-gray-500 border-gray-200'
                            }`}>
                              {row.modelStatus === 'Ready' ? 'Ready' : row.modelStatus === 'Training' ? 'In Progress' : 'Not Started'}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-muted-foreground">{row.lastUpdated ? new Date(row.lastUpdated).toLocaleString() : '—'}</td>
                        </tr>
                      );
                    })}
                    {trainingRows.length === 0 && (
                      <tr>
                        <td className="px-3 py-3 text-muted-foreground" colSpan={4}>No visual class data available.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="operations" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-3">
                <CardTitle>System status</CardTitle>
                <div className="flex items-center gap-2">
                  <Select
                    value={String(metricsWindowHours)}
                    onValueChange={(value) => setMetricsWindowHours(value === '168' ? 168 : 24)}
                  >
                    <SelectTrigger className="w-28">
                      <SelectValue placeholder="Window" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="24">24h</SelectItem>
                      <SelectItem value="168">7d</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      try {
                        setMetricsLoading(true);
                        setMetricsError(null);
                        const res = await fetch(`/api/admin/ai-verification/ops/metrics?windowHours=${metricsWindowHours}`);
                        if (!res.ok) throw new Error(`Failed to refresh metrics (${res.status})`);
                        const json = await res.json();
                        setOpsMetrics(json?.data ?? null);
                      } catch (e: any) {
                        setMetricsError(e?.message || 'Failed to refresh metrics');
                      } finally {
                        setMetricsLoading(false);
                      }
                    }}
                    disabled={metricsLoading}
                  >
                    {metricsLoading ? 'Refreshing...' : 'Refresh'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {metricsError && <div className="text-sm text-red-600">{metricsError}</div>}
              {!opsMetrics && !metricsError && (
                <p className="text-sm text-muted-foreground">No metrics available yet.</p>
              )}

              {opsMetrics && (
                <>
                  {/* Contextual alerts */}
                  {opsMetrics.queue.failedCount > 0 && (
                    <Alert variant="destructive">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertTitle>Failed Jobs</AlertTitle>
                      <AlertDescription>
                        {opsMetrics.queue.failedCount} job{opsMetrics.queue.failedCount === 1 ? '' : 's'} failed in the last {metricsWindowHours}h. These submissions will fall back to manual review.
                      </AlertDescription>
                    </Alert>
                  )}
                  {opsMetrics.verification.insufficientEvidenceRate > 0.15 && (
                    <Alert>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertTitle>High Insufficient Evidence Rate</AlertTitle>
                      <AlertDescription>
                        {formatPercent(opsMetrics.verification.insufficientEvidenceRate)} of verifications lacked enough visual evidence. Consider reviewing image quality guidelines with operators.
                      </AlertDescription>
                    </Alert>
                  )}
                  {opsMetrics.verification.unknownFormatRate > 0.1 && (
                    <Alert>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertTitle>High Unknown Format Rate</AlertTitle>
                      <AlertDescription>
                        {formatPercent(opsMetrics.verification.unknownFormatRate)} of images couldn't be classified. Check your format mappings in the Formats tab.
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Queue metrics */}
                  <div>
                    <h3 className="text-sm font-medium mb-2">Queue</h3>
                    <p className="text-xs text-muted-foreground mb-3">Jobs waiting to be processed by the AI worker.</p>
                    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-5">
                      <div className="border rounded-md p-3">
                        <p className="text-xs text-muted-foreground">Queue size</p>
                        <p className="text-lg font-semibold">{opsMetrics.queue.pendingCount + opsMetrics.queue.processingCount}</p>
                        <p className="text-xs text-muted-foreground">Jobs waiting + active</p>
                      </div>
                      <div className="border rounded-md p-3">
                        <p className="text-xs text-muted-foreground">Processing</p>
                        <p className="text-lg font-semibold">{opsMetrics.queue.processingCount}</p>
                        <p className="text-xs text-muted-foreground">Currently being analysed</p>
                      </div>
                      <div className={`border rounded-md p-3 ${opsMetrics.queue.failedCount > 0 ? 'border-red-200 bg-red-50/50' : ''}`}>
                        <p className="text-xs text-muted-foreground">Failed</p>
                        <p className={`text-lg font-semibold ${opsMetrics.queue.failedCount > 0 ? 'text-red-600' : ''}`}>{opsMetrics.queue.failedCount}</p>
                        <p className="text-xs text-muted-foreground">Will need manual review</p>
                      </div>
                      <div className="border rounded-md p-3">
                        <p className="text-xs text-muted-foreground">Retry rate</p>
                        <p className="text-lg font-semibold">{formatPercent(retryRate)}</p>
                        <p className="text-xs text-muted-foreground">Jobs needing re-attempts</p>
                      </div>
                      <div className="border rounded-md p-3">
                        <p className="text-xs text-muted-foreground">Avg processing time</p>
                        <p className="text-lg font-semibold">{formatSeconds(opsMetrics.queue.avgDoneLatencySeconds)}</p>
                        <p className="text-xs text-muted-foreground">Per completed job</p>
                      </div>
                    </div>
                  </div>

                  {/* Verification quality */}
                  <div>
                    <h3 className="text-sm font-medium mb-2">Verification Quality</h3>
                    <p className="text-xs text-muted-foreground mb-3">How well the AI is performing on processed submissions.</p>
                    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
                      <div className="border rounded-md p-3">
                        <p className="text-xs text-muted-foreground">Total verified</p>
                        <p className="text-lg font-semibold">{opsMetrics.verification.total}</p>
                        <p className="text-xs text-muted-foreground">In the last {metricsWindowHours}h</p>
                      </div>
                      <div className="border rounded-md p-3">
                        <p className="text-xs text-muted-foreground">Insufficient evidence</p>
                        <p className="text-lg font-semibold">{formatPercent(opsMetrics.verification.insufficientEvidenceRate)}</p>
                        <p className="text-xs text-muted-foreground">Image quality too low to verify</p>
                      </div>
                      <div className="border rounded-md p-3">
                        <p className="text-xs text-muted-foreground">Unknown format</p>
                        <p className="text-lg font-semibold">{formatPercent(opsMetrics.verification.unknownFormatRate)}</p>
                        <p className="text-xs text-muted-foreground">Could not classify the image</p>
                      </div>
                      <div className="border rounded-md p-3">
                        <p className="text-xs text-muted-foreground">Reviewer overrides</p>
                        <p className="text-lg font-semibold">{formatPercent(opsMetrics.verification.reviewerOverrideRate)}</p>
                        <p className="text-xs text-muted-foreground">AI result changed by reviewer</p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        {settingsFormat && (
          <Dialog open={!!settingsFormat} onOpenChange={(open) => !open && setSettingsFormat(null)}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Recognition settings</DialogTitle>
                <DialogDescription>
                  {settingsFormat.mediaFormatName} · {settingsFormat.retailerName}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <p className="text-sm font-medium">How strict should spotting be?</p>
                  <p className="text-xs text-muted-foreground">
                    Higher means fewer uncertain matches. Only change this if spotting is too noisy or too quiet.
                  </p>
                  <div className="text-xs text-muted-foreground">{settingsConfidence.toFixed(2)}</div>
                  <Slider
                    min={0.5}
                    max={0.99}
                    step={0.01}
                    value={[settingsConfidence]}
                    onValueChange={(values) => {
                      setSettingsConfidence(clampConfidence(values[0] ?? settingsConfidence));
                    }}
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    onClick={() => {
                      handleEnableAI(settingsFormat);
                      setSettingsFormat(null);
                    }}
                    disabled={savingId === settingsFormat.mediaFormatId}
                  >
                    Turn recognition on
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      await handleDisableAI(settingsFormat);
                      setSettingsFormat(null);
                    }}
                    disabled={savingId === settingsFormat.mediaFormatId}
                  >
                    Turn recognition off
                  </Button>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setSettingsFormat(null)}>
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    await handleSaveMapping(
                      { ...settingsFormat, mappingConfidence: settingsConfidence },
                      { mappingConfidence: settingsConfidence },
                    );
                    setSettingsFormat(null);
                  }}
                  disabled={savingId === settingsFormat.mediaFormatId}
                >
                  {savingId === settingsFormat.mediaFormatId ? 'Saving…' : 'Save'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {wizardFormat && (
          <FormatOnboardingWizard
            open={!!wizardFormat}
            onOpenChange={(open) => !open && setWizardFormat(null)}
            retailerId={wizardFormat.retailerId}
            mediaFormatId={wizardFormat.mediaFormatId}
            retailerName={wizardFormat.retailerName}
            mediaFormatName={wizardFormat.mediaFormatName}
            visualClassId={wizardFormat.visualClassId || null}
            onComplete={() => {
              handleSaveMapping(wizardFormat, {
                onboardingStatus: 'ACTIVE',
                supportLevel: 'AI_ASSISTED',
                mappingConfidence: clampConfidence(wizardFormat.mappingConfidence ?? 0.85),
                decisionReason: wizardFormat.decisionReason ?? 'Enabled from onboarding wizard',
              });
              setWizardFormat(null);
            }}
          />
        )}
      </Tabs>
    </div>
  );
}
